import type { Answer, BaseItemSchema, Item } from "@memorang/types";
import { resolveMaxScore } from "./scoring/max-score";
import { isQuestionTypeTag, isSkillTag } from "./tag";
import { getSubmittedRawInput } from "./user-submitted-answer";

const UNREVIEWABLE_ITEM_TYPES = ["INTRO_SCREEN", "PISW", "PISW_CORE"];

const hasUserSubmittedAnswer = (answer: Answer) =>
	typeof answer.userSubmittedAnswer === "boolean"
		? answer.userSubmittedAnswer
		: getSubmittedRawInput(answer) != null;

// Skip answer-events on AI-scored items still attach a hollow aiResponse
// (feedback: "", no subskills), so presence alone cannot mean answered.
const hasAiResponseContent = (aiResponse: Answer["aiResponse"]) =>
	aiResponse != null &&
	(Boolean(aiResponse.explanation) ||
		Boolean(aiResponse.feedback) ||
		Boolean(aiResponse.transcription) ||
		Boolean(aiResponse.answer) ||
		(aiResponse.score ?? 0) > 0 ||
		(aiResponse.displayScore ?? 0) > 0 ||
		(aiResponse.subskillAssessments?.length ?? 0) > 0);

export const isAttempted = (item: Item) => item.distribution !== "none";

export const isAnswered = (answer?: Answer | null) => {
	if (!answer) {
		return false;
	}

	return (
		// Partial credit arrives as score 0 with points > 0, so these are
		// independent signals — a `score ?? points` fallthrough would hide it.
		(answer.score ?? 0) > 0 ||
		(answer.points ?? 0) > 0 ||
		Boolean(answer.userResponseText) ||
		(answer.selectedChoiceIds?.length ?? 0) > 0 ||
		(answer.selectedOrder?.length ?? 0) > 0 ||
		Object.keys(answer.selectedBlankValues ?? {}).length > 0 ||
		Object.keys(answer.answeredFormStates ?? {}).length > 0 ||
		hasUserSubmittedAnswer(answer) ||
		hasAiResponseContent(answer.aiResponse)
	);
};

// Not the inverse of isAnswered: only response slots count, so a graded-zero
// answer with no response still reads as skipped.
export const isAnswerSkipped = (
	answer?: Answer | null,
	liveResponseText?: string,
) => {
	const text =
		answer?.aiResponse?.answer?.trim() ||
		answer?.userResponseText?.trim() ||
		liveResponseText?.trim();
	return !(
		(answer?.selectedChoiceIds?.length ?? 0) > 0 ||
		Object.keys(answer?.selectedBlankValues ?? {}).length > 0 ||
		(answer?.selectedOrder?.length ?? 0) > 0 ||
		Boolean(text)
	);
};

export const isAnsweredCorrectly = (answer: Answer) => {
	return isAnswered(answer) && answer.correct === true;
};

export const isItemAnswered = ({
	item,
	isSummativeTest = false,
	inReviewMode = false,
}: {
	item: BaseItemSchema;
	isSummativeTest?: boolean;
	inReviewMode?: boolean;
}) => {
	if (isSummativeTest) {
		return inReviewMode && isAnswered(item.answer);
	}

	return isAnswered(item.answer);
};

export const isItemSkipped = (item: BaseItemSchema) => {
	return (
		item.answer != null &&
		item.answer?.correct !== false &&
		!isAnswered(item.answer)
	);
};

export const isItemAnsweredCorrectly = (item: BaseItemSchema) => {
	return item.answer != null && isAnsweredCorrectly(item.answer);
};

export const isItemMarked = (item: BaseItemSchema) => {
	return Boolean(item.answer?.marked);
};

export const isItemReviewable = (item: BaseItemSchema) => {
	return !UNREVIEWABLE_ITEM_TYPES.includes(item.type);
};

export const getItemSkill = (item: BaseItemSchema) => {
	return item.tags?.find((tag) => isSkillTag(tag));
};

export const getItemQuestionType = (item: BaseItemSchema) => {
	return item.tags?.find((tag) => isQuestionTypeTag(tag));
};

export const getItemPreviewText = (item: BaseItemSchema) => {
	const stimulus = item.content?.stimulus;
	const media = stimulus?.media?.[0];
	return (
		item.content?.stem?.text ??
		stimulus?.text ??
		media?.transcript ??
		media?.transcription ??
		media?.description ??
		media?.caption ??
		item.response?.instruction
	);
};

const sumSubskillScores = (answer: Answer) => {
	const scores = (answer.aiResponse?.subskillAssessments ?? []).flatMap(
		(assessment) => (assessment.score != null ? [assessment.score] : []),
	);
	if (scores.length === 0) {
		return undefined;
	}
	return scores.reduce((sum, score) => sum + score, 0);
};

const sumSubskillDisplayScores = (answer: Answer) => {
	const scores = (answer.aiResponse?.subskillAssessments ?? []).flatMap(
		(assessment) =>
			assessment.displayScore != null ? [assessment.displayScore] : [],
	);
	if (scores.length === 0) {
		return undefined;
	}
	return scores.reduce((sum, score) => sum + score, 0);
};

// Backend display policy rounds per criterion then sums; re-rounding the raw
// sum here can disagree with it (e.g. 23.25 raw vs 24 displayed).
export const resolveAnswerDisplayScore = (answer?: Answer) => {
	return answer == null
		? undefined
		: (answer.displayPoints ?? sumSubskillDisplayScores(answer));
};

// `answer.points` can be normalized-scale for AI items, so within the raw chain
// the explicit raw signals win over it.
export const resolveAnswerRawScore = (answer?: Answer) =>
	answer == null
		? undefined
		: (answer.aiResponse?.score ??
			answer.rawPoints ??
			sumSubskillScores(answer) ??
			answer.points);

export const getItemScore = (item: BaseItemSchema) => {
	const maxScore = resolveMaxScore(item.scoring);

	// predicted_mastery is a 0-100 percentage, so it can only stand in for the
	// score on percent-scale items (no point maximum), never in an x/y fraction.
	const isPointScale = maxScore != null && maxScore > 0;

	return {
		score:
			resolveAnswerDisplayScore(item.answer) ??
			(isPointScale ? undefined : item.answer?.predicted_mastery) ??
			resolveAnswerRawScore(item.answer) ??
			0,
		maxScore,
	};
};

export const hasItemScoreData = (item: BaseItemSchema) => {
	const maxScore = resolveMaxScore(item.scoring);
	if (maxScore != null && maxScore > 0) {
		return true;
	}
	return (
		resolveAnswerDisplayScore(item.answer) != null ||
		item.answer?.predicted_mastery != null ||
		resolveAnswerRawScore(item.answer) != null
	);
};

export const getItemScorePercentage = (item: BaseItemSchema) => {
	const { score, maxScore } = getItemScore(item);
	return maxScore != null && maxScore > 0 ? (score / maxScore) * 100 : score;
};

export const getItemScoreText = (item: BaseItemSchema) => {
	const { score, maxScore } = getItemScore(item);

	if (maxScore != null && maxScore > 0) {
		return `${Math.round(score)}/${maxScore}`;
	}

	return `${Math.round(score)}%`;
};
